import { getAddonsEvents } from '../common/addons-events'
import { AddonsStatusEnum, IAddons } from '../types/addons'
import { IPlanBoost, SubscriptionPlan, SubscriptionPlanEnum } from '../types/company'

export const PLAN_BOOST_DEFAULT_DAYS = 14
export const PLAN_BOOST_MAX_DAYS = 90
export const PLAN_BOOST_MAX_TOTAL_DAYS = 365
export const PLAN_BOOST_ENDING_SOON_DAYS = 3

export const DAY_IN_MS = 24 * 60 * 60 * 1000

export type PlanBoostPhase = 'none' | 'active' | 'ending' | 'downgrading' | 'expired' | 'converted'

export type PlanBoostIneligibilityReason = 'not-core' | 'canceled' | 'boost-in-progress' | 'old-price' | 'paid-non-core-addons' | 'scheduled-billing-change'

export interface IPlanBoostCompany {
  plan?: string
  subscriptionStatus?: string | null
  planBoost?: (Pick<IPlanBoost, 'status' | 'expiresAt'> & Partial<IPlanBoost>) | null
}

export interface IPlanBoostBilling {
  subscriptionStatus?: string | null
  subscriptionPeriod?: string | null
  hasScheduledChange?: boolean | null
  cancelAtPeriodEnd?: boolean | null
  nextSubscriptionPlan?: string | null
  nextSubscriptionPeriod?: string | null
  isOnOldPrice?: boolean
}

export interface IGetPlanBoostIneligibilityReasonParams {
  company: IPlanBoostCompany
  billing?: IPlanBoostBilling | null
  addons?: Pick<IAddons, 'addons' | 'automations' | 'integrations'> | null
  now?: Date
}

const CANCELED_STATUS = 'canceled'
const UNBILLED_ADDON_STATUSES: string[] = [AddonsStatusEnum.GRANT_PERIOD, AddonsStatusEnum.CANCELED]

export function addPlanBoostDays(date: Date | string, days: number): Date {
  return new Date(new Date(date).getTime() + days * DAY_IN_MS)
}

export function isPlanBoostActive(company?: IPlanBoostCompany | null, now: Date = new Date()): boolean {
  const planBoost = company?.planBoost
  return (
    company?.plan === SubscriptionPlanEnum.core &&
    planBoost?.status === 'active' &&
    now.getTime() < new Date(planBoost.expiresAt).getTime() &&
    company.subscriptionStatus !== CANCELED_STATUS
  )
}

export function getEffectivePlan(company?: IPlanBoostCompany | null, now: Date = new Date()): SubscriptionPlan | undefined {
  if (isPlanBoostActive(company, now)) {
    return SubscriptionPlanEnum.complete
  }
  return company?.plan as SubscriptionPlan | undefined
}

export function hasCompleteFeatures(company?: IPlanBoostCompany | null, now: Date = new Date()): boolean {
  return getEffectivePlan(company, now) === SubscriptionPlanEnum.complete
}

export function isPlanBoostEndingSoon(company?: IPlanBoostCompany | null, now: Date = new Date()): boolean {
  const expiresAt = company?.planBoost?.expiresAt
  if (!expiresAt || !isPlanBoostActive(company, now)) {
    return false
  }
  return new Date(expiresAt).getTime() - now.getTime() <= PLAN_BOOST_ENDING_SOON_DAYS * DAY_IN_MS
}

export function getPlanBoostPhase(company?: IPlanBoostCompany | null, now: Date = new Date()): PlanBoostPhase {
  const planBoost = company?.planBoost
  if (!planBoost) {
    return 'none'
  }
  if (planBoost.status === 'converted') {
    return 'converted'
  }
  if (planBoost.status === 'active') {
    return now.getTime() < new Date(planBoost.expiresAt).getTime() ? 'active' : 'ending'
  }
  return planBoost.cleanupAt && !planBoost.downgradedAt ? 'downgrading' : 'expired'
}

export function getPlanBoostIneligibilityReason(params: IGetPlanBoostIneligibilityReasonParams): PlanBoostIneligibilityReason | undefined {
  const { company, billing, addons, now = new Date() } = params
  if (company.plan !== SubscriptionPlanEnum.core) {
    return 'not-core'
  }
  if (company.subscriptionStatus === CANCELED_STATUS || billing?.subscriptionStatus === CANCELED_STATUS) {
    return 'canceled'
  }
  if (['active', 'ending', 'downgrading'].includes(getPlanBoostPhase(company, now))) {
    return 'boost-in-progress'
  }
  if (billing?.isOnOldPrice) {
    return 'old-price'
  }
  if (hasPaidNonCoreAddon(addons)) {
    return 'paid-non-core-addons'
  }
  if (billing?.hasScheduledChange || billing?.cancelAtPeriodEnd || hasPendingPlanOrPeriodChange(company, billing)) {
    return 'scheduled-billing-change'
  }
  return undefined
}

// The boost expiry removes every add-on Core doesn't include, like a real downgrade, but leaves billing alone,
// so a company still paying for one of them would keep being billed for an add-on it no longer has.
function hasPaidNonCoreAddon(addons: IGetPlanBoostIneligibilityReasonParams['addons']): boolean {
  if (!addons) {
    return false
  }
  const coreTypes = getAddonsEvents()
    .filter((addonEvent) => addonEvent.includedIn?.includes(SubscriptionPlanEnum.core))
    .map((addonEvent) => addonEvent.type)
  return [...(addons.addons ?? []), ...(addons.automations ?? []), ...(addons.integrations ?? [])].some(
    (addon) => !coreTypes.includes(addon.type) && !UNBILLED_ADDON_STATUSES.includes(addon.status)
  )
}

// Stripe billing records always carry the next plan and period, equal to the current ones when nothing is scheduled.
function hasPendingPlanOrPeriodChange(company: IPlanBoostCompany, billing?: IPlanBoostBilling | null): boolean {
  const changesPlan = Boolean(billing?.nextSubscriptionPlan) && billing?.nextSubscriptionPlan !== company.plan
  const changesPeriod = Boolean(billing?.nextSubscriptionPeriod) && billing?.nextSubscriptionPeriod !== billing?.subscriptionPeriod
  return changesPlan || changesPeriod
}
